/**
 * Attribute Icon Renderer - Attribute node icon
 *
 * Renders a gear/settings icon used for representing attribute nodes
 * in the graph visualization.
 */

import { getIconConfig } from '@/components/icons/nodeIcons/config/iconConfig';
import { getThemeColors } from '@/components/icons/nodeIcons/config/themeColors';
import {
  calculateIconPosition,
  drawCircleBackground,
  setupCanvas,
} from '@/components/icons/nodeIcons/renderers/base';

/**
 * Create attribute icon data URL
 *
 * Generates a gear/settings icon with circular background for use as
 * an attribute node image in the graph visualization.
 *
 * @param currentTheme - Current theme ('light' or 'dark')
 * @param isHighlighted - Whether the icon should appear highlighted
 * @returns Data URL string of the rendered icon
 */
export const createAttributeIconDataURL = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean = false
): string => {
  const config = getIconConfig('attribute');
  const colors = getThemeColors('attribute', currentTheme, isHighlighted);
  const { canvas, ctx, size, centerX, centerY } = setupCanvas(config);

  // Draw circle background and border
  drawCircleBackground(ctx, centerX, centerY, size, config.scale, colors);

  // Calculate icon position
  const {
    x: gearX,
    y: gearY,
    scale: gearScale,
  } = calculateIconPosition(centerX, centerY, size, config);

  // Set gear color
  ctx.fillStyle = colors.icon;

  // Start drawing the gear path
  ctx.save();
  ctx.translate(gearX, gearY);
  ctx.scale(gearScale, gearScale);

  // Draw the gear SVG path
  ctx.beginPath();

  // Outer gear teeth
  ctx.moveTo(14.9474, 6.50626);
  ctx.lineTo(14.1074, 6.20626);
  ctx.bezierCurveTo(13.9858, 6.1646, 13.8745, 6.09717, 13.7813, 6.00858);
  ctx.bezierCurveTo(13.6881, 5.91999, 13.6152, 5.81232, 13.5674, 5.69293);
  ctx.bezierCurveTo(13.5162, 5.5763, 13.4897, 5.45031, 13.4897, 5.32293);
  ctx.bezierCurveTo(13.4897, 5.19555, 13.5162, 5.06955, 13.5674, 4.95293);
  ctx.lineTo(14.0007, 4.1396);
  ctx.bezierCurveTo(14.1416, 3.84476, 14.1875, 3.51352, 14.1322, 3.19149);
  ctx.bezierCurveTo(14.077, 2.86946, 13.9233, 2.57248, 13.6922, 2.34144);
  ctx.bezierCurveTo(13.4612, 2.11041, 13.1642, 1.95668, 12.8422, 1.90143);
  ctx.bezierCurveTo(12.5202, 1.84617, 12.1889, 1.89212, 11.8941, 2.03293);
  ctx.lineTo(11.0807, 2.4196);
  ctx.bezierCurveTo(10.9636, 2.46892, 10.8378, 2.49433, 10.7107, 2.49433);
  ctx.bezierCurveTo(10.5837, 2.49433, 10.4579, 2.46892, 10.3407, 2.4196);
  ctx.bezierCurveTo(10.2227, 2.36966, 10.1162, 2.29595, 10.0279, 2.20304);
  ctx.bezierCurveTo(9.93954, 2.11013, 9.8713, 2.00003, 9.82741, 1.8796);
  ctx.lineTo(9.52741, 1.03293);
  ctx.bezierCurveTo(9.41698, 0.725356, 9.21427, 0.459383, 8.94698, 0.271356);
  ctx.bezierCurveTo(
    8.6797,
    0.0833298,
    8.36087,
    -0.0175781,
    8.03408,
    -0.0175781
  );
  ctx.bezierCurveTo(7.70728, -0.0175781, 7.38845, 0.0833298, 7.12117, 0.271356);
  ctx.bezierCurveTo(6.85388, 0.459383, 6.65117, 0.725356, 6.54074, 1.03293);
  ctx.lineTo(6.20741, 1.8996);
  ctx.bezierCurveTo(6.16351, 2.02003, 6.09528, 2.13013, 6.00696, 2.22304);
  ctx.bezierCurveTo(5.91864, 2.31595, 5.81214, 2.38966, 5.69408, 2.4396);
  ctx.bezierCurveTo(5.57696, 2.48892, 5.45116, 2.51433, 5.32408, 2.51433);
  ctx.bezierCurveTo(5.19699, 2.51433, 5.0712, 2.48892, 4.95408, 2.4396);
  ctx.lineTo(4.14074, 2.03293);
  ctx.bezierCurveTo(3.84591, 1.89212, 3.51467, 1.84617, 3.19264, 1.90143);
  ctx.bezierCurveTo(2.87061, 1.95668, 2.57363, 2.11041, 2.34259, 2.34144);
  ctx.bezierCurveTo(2.11155, 2.57248, 1.95782, 2.86946, 1.90257, 3.19149);
  ctx.bezierCurveTo(1.84732, 3.51352, 1.89326, 3.84476, 2.03408, 4.1396);
  ctx.lineTo(2.42074, 4.95293);
  ctx.bezierCurveTo(2.47197, 5.06955, 2.49842, 5.19555, 2.49842, 5.32293);
  ctx.bezierCurveTo(2.49842, 5.45031, 2.47197, 5.5763, 2.42074, 5.69293);
  ctx.bezierCurveTo(2.37298, 5.81232, 2.30001, 5.91999, 2.20681, 6.00858);
  ctx.bezierCurveTo(2.11362, 6.09717, 2.00239, 6.1646, 1.88074, 6.20626);
  ctx.lineTo(1.04074, 6.50626);
  ctx.bezierCurveTo(0.733168, 6.61669, 0.467196, 6.8194, 0.279169, 7.08669);
  ctx.bezierCurveTo(
    0.0911423,
    7.35397,
    -0.00976563,
    7.6728,
    -0.00976562,
    7.9996
  );
  ctx.bezierCurveTo(
    -0.00976562,
    8.32639,
    0.0911423,
    8.64522,
    0.279169,
    8.91251
  );
  ctx.bezierCurveTo(0.467196, 9.17979, 0.733168, 9.3825, 1.04074, 9.49293);
  ctx.lineTo(1.88074, 9.79293);
  ctx.bezierCurveTo(2.00239, 9.83459, 2.11362, 9.90202, 2.20681, 9.99061);
  ctx.bezierCurveTo(2.30001, 10.0792, 2.37298, 10.1869, 2.42074, 10.3063);
  ctx.bezierCurveTo(2.47197, 10.4229, 2.49842, 10.5489, 2.49842, 10.6763);
  ctx.bezierCurveTo(2.49842, 10.8036, 2.47197, 10.9296, 2.42074, 11.0463);
  ctx.lineTo(2.03408, 11.8596);
  ctx.bezierCurveTo(1.88129, 12.1555, 1.82591, 12.4921, 1.87582, 12.8213);
  ctx.bezierCurveTo(1.92573, 13.1506, 2.07839, 13.4556, 2.31201, 13.6929);
  ctx.bezierCurveTo(2.54562, 13.9302, 2.84824, 14.0877, 3.17666, 14.1427);
  ctx.bezierCurveTo(3.50507, 14.1978, 3.84249, 14.1477, 4.14074, 13.9996);
  ctx.lineTo(4.95408, 13.6196);
  ctx.bezierCurveTo(5.07026, 13.5667, 5.19643, 13.5394, 5.32408, 13.5394);
  ctx.bezierCurveTo(5.45172, 13.5394, 5.57789, 13.5667, 5.69408, 13.6196);
  ctx.bezierCurveTo(5.81279, 13.6685, 5.91986, 13.7418, 6.00831, 13.8349);
  ctx.bezierCurveTo(6.09676, 13.9279, 6.1646, 14.0386, 6.20741, 14.1596);
  ctx.lineTo(6.50741, 14.9996);
  ctx.bezierCurveTo(6.61784, 15.3072, 6.82055, 15.5731, 7.08783, 15.7612);
  ctx.bezierCurveTo(7.35512, 15.9492, 7.67395, 16.0501, 8.00074, 16.0501);
  ctx.bezierCurveTo(8.32754, 16.0501, 8.64636, 15.9492, 8.91365, 15.7612);
  ctx.bezierCurveTo(9.18094, 15.5731, 9.38365, 15.3072, 9.49408, 14.9996);
  ctx.lineTo(9.79408, 14.1596);
  ctx.bezierCurveTo(9.83688, 14.0386, 9.90473, 13.9279, 9.99318, 13.8349);
  ctx.bezierCurveTo(10.0816, 13.7418, 10.1887, 13.6685, 10.3074, 13.6196);
  ctx.bezierCurveTo(10.4236, 13.5667, 10.5498, 13.5394, 10.6774, 13.5394);
  ctx.bezierCurveTo(10.8051, 13.5394, 10.9312, 13.5667, 11.0474, 13.6196);
  ctx.lineTo(11.8607, 13.9996);
  ctx.bezierCurveTo(12.1599, 14.1605, 12.503, 14.2208, 12.839, 14.1714);
  ctx.bezierCurveTo(13.1751, 14.122, 13.4864, 13.9656, 13.7266, 13.7254);
  ctx.bezierCurveTo(13.9668, 13.4852, 14.1232, 13.174, 14.1726, 12.8379);
  ctx.bezierCurveTo(14.2219, 12.5018, 14.1617, 12.1587, 14.0007, 11.8596);
  ctx.lineTo(13.6141, 11.0463);
  ctx.bezierCurveTo(13.5628, 10.9296, 13.5364, 10.8036, 13.5364, 10.6763);
  ctx.bezierCurveTo(13.5364, 10.5489, 13.5628, 10.4229, 13.6141, 10.3063);
  ctx.bezierCurveTo(13.6618, 10.1869, 13.7348, 10.0792, 13.828, 9.99061);
  ctx.bezierCurveTo(13.9212, 9.90202, 14.0324, 9.83459, 14.1541, 9.79293);
  ctx.lineTo(14.9941, 9.49293);
  ctx.bezierCurveTo(15.3016, 9.3825, 15.5676, 9.17979, 15.7556, 8.91251);
  ctx.bezierCurveTo(15.9437, 8.64522, 16.0446, 8.32639, 16.0446, 7.9996);
  ctx.bezierCurveTo(16.0446, 7.6728, 15.9437, 7.35397, 15.7556, 7.08669);
  ctx.bezierCurveTo(15.5676, 6.8194, 15.3016, 6.61669, 14.9941, 6.50626);
  ctx.lineTo(14.9474, 6.50626);

  // Center gear section
  ctx.moveTo(8.00074, 11.1929);
  ctx.bezierCurveTo(7.37209, 11.1777, 6.76057, 10.985, 6.2368, 10.637);
  ctx.bezierCurveTo(5.71303, 10.289, 5.29838, 9.7999, 5.04074, 9.22626);
  ctx.bezierCurveTo(4.8791, 8.83762, 4.79571, 8.4209, 4.79537, 7.99998);
  ctx.bezierCurveTo(4.79503, 7.57905, 4.87773, 7.1622, 5.03875, 6.77329);
  ctx.bezierCurveTo(5.19976, 6.38438, 5.43592, 6.03106, 5.73369, 5.73357);
  ctx.bezierCurveTo(6.03147, 5.43607, 6.38502, 5.20024, 6.77408, 5.0396);
  ctx.bezierCurveTo(7.56017, 4.72576, 8.43775, 4.73132, 9.2198, 5.0551);
  ctx.bezierCurveTo(10.0019, 5.37888, 10.6265, 5.99528, 10.9607, 6.77293);
  ctx.bezierCurveTo(11.1224, 7.16158, 11.2058, 7.57829, 11.2061, 7.99922);
  ctx.bezierCurveTo(11.2065, 8.42014, 11.1238, 8.83699, 10.9627, 9.2259);
  ctx.bezierCurveTo(10.8017, 9.61481, 10.5656, 9.96813, 10.2678, 10.2656);
  ctx.bezierCurveTo(9.97001, 10.5631, 9.61647, 10.7989, 9.22741, 10.9596);
  ctx.bezierCurveTo(8.83924, 11.122, 8.42142, 11.2015, 8.00074, 11.1929);
  ctx.closePath();

  ctx.fill();
  ctx.restore();

  // Convert to data URL with higher quality
  return canvas.toDataURL('image/png', 1.0);
};
